import { useMemo } from "react";
import { useSelector } from "react-redux";

import { isObject } from "@cayuse/shared-react.utils.type-checks";

import {
	COLUMN_HEADER_KEYS,
	FORM_ACCESS_LEVELS,
	SUBMISSION_OWNER,
	ROLE_SP_AWARD_ADMINISTRATOR,
	ROLE_SP_AWARD_EDITOR,
	ROLE_SP_PROPOSAL_ADMINISTRATOR,
	ROLE_SP_PROPOSAL_EDITOR,
	ROLE_SP_SYSTEM_ADMINISTRATOR,
	RESEARCH_TEAM_MEMBER,
	ROLE_SP_SUBAWARD_ADMINISTRATOR,
	ROLE_SP_SUBAWARD_EDITOR,
} from "globals/constants";
import { useHasRoles } from "hooks";
import { shellSelectors, userAccessesSelectors } from "store/selectors";

export const findRole = (formType, isEditor = false) => {
	let searchRoleName = null;
	if (formType === COLUMN_HEADER_KEYS.AWARD) {
		searchRoleName = isEditor ? ROLE_SP_AWARD_EDITOR : ROLE_SP_AWARD_ADMINISTRATOR;
	} else if (formType === COLUMN_HEADER_KEYS.PROPOSAL) {
		searchRoleName = isEditor ? ROLE_SP_PROPOSAL_EDITOR : ROLE_SP_PROPOSAL_ADMINISTRATOR;
	} else if (formType === COLUMN_HEADER_KEYS.SUBAWARD) {
		searchRoleName = isEditor ? ROLE_SP_SUBAWARD_EDITOR : ROLE_SP_SUBAWARD_ADMINISTRATOR;
	}

	return searchRoleName;
};

export const useFormAccesses = (formType, options) => {
	// Get array of forms that the user has access to
	formType = formType.toUpperCase();
	const { checkParentAccess = false } = isObject(options) ? options : {};

	const currentProjectAccessTypes = useSelector(state =>
		checkParentAccess
			? userAccessesSelectors.parentSubmissionProjectAccessTypes(state)
			: userAccessesSelectors.currentProjectAccessTypes(state)
	);
	const { userGroupRoles: userAccesses = [] } = useSelector(shellSelectors.userAccess);

	// Check For Admin Level Access
	const {
		[ROLE_SP_SYSTEM_ADMINISTRATOR]: isSystemAdmin
	} = useHasRoles([
		ROLE_SP_SYSTEM_ADMINISTRATOR
	]);

	const hasAdminAccess = useMemo(
		() => {
			if (!!userAccesses.some(v => ROLE_SP_SYSTEM_ADMINISTRATOR === v.label)) {
				// has access when user has 'SP System Administrator' role.
				return true;
			}

			// has access when user has 'SP Proposal/Award/Subaward Administrator' role with root unit and subunits.
			const role = findRole(formType);

			return !!(role && userAccesses.find(v => role === v?.label));
		},
		[ formType, userAccesses ]
	);

	const isAdmin = useMemo(
		() => {
			const hasAccessByRecordAccesses = currentProjectAccessTypes.some(access => FORM_ACCESS_LEVELS[formType].ADMIN.includes(access));

			if (hasAccessByRecordAccesses) {
				// has access when record includes specific accesses in Proposal/Award/Subaward record.
				return true;
			}

			if (!!userAccesses.some(v => ROLE_SP_SYSTEM_ADMINISTRATOR === v.label)) {
				// has access when user has 'SP System Administrator' role.
				return true;
			}

			// has access when user has 'SP Proposal/Award/Subaward Administrator' role with root unit and subunits.
			const role = findRole(formType);

			return !!(role && userAccesses.find(v => role === v?.label)?.hasRootUnitWithSubUnits);
		},
		[ currentProjectAccessTypes, formType, userAccesses ]
	);


	const isEditor = useMemo(
		() => {
			const hasAccessByRecordAccesses = currentProjectAccessTypes.some(access => FORM_ACCESS_LEVELS[formType].EDITOR.includes(access));

			if (hasAccessByRecordAccesses) {
				// has access when record includes specific accesses in Proposal/Award/Subaward record.
				return true;
			}

			// has access when user has 'SP Proposal/Award/Subaward Administrator' role with root unit and subunits.
			const role = findRole(formType, true);

			return !!(role && userAccesses.find(v => role === v?.label)?.hasRootUnitWithSubUnits);
		},
		[ currentProjectAccessTypes, formType, userAccesses ]
	);

	const hasSubawardCreateAccess = useMemo(
		() => {
			if (!!userAccesses.some(v => ROLE_SP_SYSTEM_ADMINISTRATOR === v.label)) {
				// has access when user has 'SP System Administrator' role.
				return true;
			}

			const roles = [ ROLE_SP_SUBAWARD_EDITOR, ROLE_SP_SUBAWARD_ADMINISTRATOR ];

			return roles.some(role => userAccesses.find(v => v.label === role));
		},
		[ userAccesses ]
	);

	// Check For Reviewer Level Access
	const isReviewer = useMemo(
		() => currentProjectAccessTypes.some(access => FORM_ACCESS_LEVELS[formType].REVIEWER.includes(access)),
		[ currentProjectAccessTypes, formType ]
	);

	// Check For Viewer Level Access
	const isViewer = useMemo(
		() => currentProjectAccessTypes.some(access => FORM_ACCESS_LEVELS[formType].VIEWER.includes(access)),
		[ currentProjectAccessTypes, formType ],
	);

	const isRootUnitAdmin = useMemo(
		() => {
			const role = findRole(formType);

			return !!(role && userAccesses.find(v => role === v?.label)?.hasRootUnitWithSubUnits);
		},
		[ formType, userAccesses ]
	);

	const hasAdminRole = useMemo(
		() => {
			const role = findRole(formType);

			return !!(role && userAccesses.find(v => role === v?.label));
		},
		[ formType, userAccesses ]
	);

	const isSystemAdministrator = useMemo(
		() => !!userAccesses.some(v => ROLE_SP_SYSTEM_ADMINISTRATOR === v.label),
		[ userAccesses ]
	);

	const hasAccessToChangeStatus = useMemo(
		() => {
			const hasOwnerAccess = currentProjectAccessTypes.includes(SUBMISSION_OWNER);
			const hasOtherProjectLevelAdminAccess = currentProjectAccessTypes
				.some(access => FORM_ACCESS_LEVELS[formType].ADMIN.includes(access)
					&& SUBMISSION_OWNER !== access
					&& RESEARCH_TEAM_MEMBER !== access);
			const hasAdminAccesses = isSystemAdministrator || isRootUnitAdmin || hasOtherProjectLevelAdminAccess;

			return hasAdminAccesses || (hasOwnerAccess && hasAdminRole);
		},
		[ currentProjectAccessTypes, formType, isRootUnitAdmin, isSystemAdministrator, hasAdminRole ]
	);

	const hasEditorAccess = useMemo(
		() => {
			const hasOwnerAccess = currentProjectAccessTypes.includes(SUBMISSION_OWNER);
			const editorRole = findRole(formType, true);
			const isEditorWithOwnerAccess = editorRole && hasOwnerAccess && userAccesses.some(v => editorRole === v?.label);

			return isSystemAdmin || isEditor || isEditorWithOwnerAccess;
		}, [ currentProjectAccessTypes, formType, isEditor, isSystemAdmin, userAccesses ]
	);

	// Memorize the output to be returned
	return useMemo(() => ({
		manage: isAdmin || isSystemAdmin,
		view: isAdmin || isEditor || isReviewer || isViewer,
		updateForm: isAdmin || isEditor,
		updateAdminTeam: isAdmin || isEditor,
		addContent: isAdmin || isEditor || isReviewer,
		updateContent: hasAdminAccess,
		review: isAdmin || isEditor || isReviewer,
		certify: isAdmin || isEditor,
		changeStatus: hasAccessToChangeStatus,
		systemAdmin: isSystemAdmin,
		editor: hasEditorAccess,
		subawardCreate: hasSubawardCreateAccess,
	}),
	[
		isAdmin,
		isSystemAdmin,
		isEditor,
		isReviewer,
		isViewer,
		hasAdminAccess,
		hasAccessToChangeStatus,
		hasEditorAccess,
		hasSubawardCreateAccess ]
	);
};

export default useFormAccesses;
